import { useState } from "react";
import { ToWords } from "to-words";
import DownloadButton from "../DownloadButton";
import { convertAmountToWords } from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";
import "../../styles/components/DownloadFormatModal.css";

const detailCostFields = [
  { label: "RM Cost", keys: ["rm_cost", "raw_mtl_cost", "raw_material_cost"] },
  { label: "Total Process Cost", keys: ["total_process_cost", "process_cost"] },
  { label: "Finishing Cost", keys: ["finishing_cost", "finish_cost"] },
  { label: "Boughtout Cost", keys: ["boughtout_cost", "bought_out_cost", "bought_out"] },
  { label: "Overheads", keys: ["overheads", "overhead_cost", "overheads_cost", "overhead_and_handling_cost"] },
  { label: "Profit", keys: ["profit", "profit_cost", "profict_cost"] },
];

const normalizeCostField = (value) =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export const SQInvoiceLayout = ({
  headDetails = {},
  companyData = {},
  itemDetails = [],
  rowData = [],
  invoiceTableData,
  selectedReportName,
  initialPrintFormat,
}) => {
  const [printFormat, setPrintFormat] = useState(initialPrintFormat === "detail" ? "detail" : "summary");
  const [pendingPrintFormat, setPendingPrintFormat] = useState(initialPrintFormat === "detail" ? "detail" : "summary");
  const [isFormatOpen, setIsFormatOpen] = useState(!initialPrintFormat);
  const toWords = new ToWords();

  const organizationData =
    JSON.parse(
      localStorage.getItem("organisation") ||
        localStorage.getItem("organization") ||
        "{}",
    ) || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr || "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    if (
      amount === undefined ||
      amount === null ||
      amount === "" ||
      isNaN(amount)
    ) {
      return "0.00";
    }
    return Number(amount).toFixed(2);
  };

  const formatCompanyAddress = (orgData) => {
    if (!orgData) return "";
    const parts = [
      orgData.address?.trim(),
      orgData.city?.trim(),
      orgData.state?.trim(),
    ].filter(Boolean);
    const pin = orgData.pincode ? `- ${orgData.pincode}` : "";
    return parts.length > 0 ? `${parts.join(", ")} ${pin}`.trim() : "";
  };

  const getItemFieldValue = (item, keys) => {
    if (!item) return "";
    for (const key of keys) {
      if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
        return item[key];
      }
    }
    return "";
  };

  const getCostValue = (item, field) => {
    const names = [field.label, ...field.keys].map(normalizeCostField);
    const definition = rowData.find((column) =>
      [column.title, column.display_name, column.field_name].some((name) =>
        names.includes(normalizeCostField(name)),
      ),
    );
    const itemKeys = Object.keys(item).filter((key) =>
      names.includes(normalizeCostField(key)),
    );
    const value = getItemFieldValue(item, [
      ...(definition?.field_name ? [definition.field_name] : []),
      ...field.keys,
      ...itemKeys,
    ]);
    return value === "" ? "" : formatCurrency(value);
  };

  const calculateTotals = () => {
    let itemsSubtotal = 0;
    if (Array.isArray(itemDetails) && itemDetails.length > 0) {
      itemDetails.forEach((item) => {
        const qty = parseFloat(
          getItemFieldValue(item, [
            "quote_qty",
            "quoteQty",
            "qty",
            "quantity",
            "order_qty",
          ]) || 0,
        );
        const rate = parseFloat(
          getItemFieldValue(item, [
            "rate_piece",
            "ratePiece",
            "rate",
            "price",
            "unit_price",
            "unitPrice",
          ]) || 0,
        );
        const amt = parseFloat(item.amount || item.total_amount || 0);
        if (amt > 0) {
          itemsSubtotal += amt;
        } else if (qty > 0 && rate > 0) {
          itemsSubtotal += qty * rate;
        }
      });
    }

    const freightCharges = parseFloat(
      headDetails?.freight_charges || headDetails?.freight || 0,
    );
    const pfCharges = parseFloat(
      headDetails?.pf_charges ||
        headDetails?.packing_and_forwarding_charges ||
        headDetails?.packing_and_forwarding ||
        0,
    );
    const grossTotal = itemsSubtotal + freightCharges + pfCharges;
    const discount = parseFloat(
      headDetails?.discount || headDetails?.discount_amount || 0,
    );
    const netTotal = headDetails?.net_amt
      ? parseFloat(headDetails.net_amt)
      : grossTotal - discount;

    return {
      freightCharges,
      pfCharges,
      grossTotal,
      discount,
      netTotal,
    };
  };

  const { freightCharges, pfCharges, grossTotal, discount, netTotal } =
    calculateTotals();

  const amountInWords =
    netTotal > 0 ? convertAmountToWords(netTotal, toWords) : "";

  const remarkValue = String(
    headDetails?.remark ||
      headDetails?.remarks ||
      headDetails?.description ||
      "",
  ).trim();
  const displayedRemark = remarkValue && remarkValue !== "-" ? remarkValue : "-";

  const getRevisionValue = () => {
    if (
      headDetails?.revision_no !== undefined &&
      headDetails?.revision_no !== null &&
      headDetails?.revision_no !== ""
    ) {
      const num = Number(headDetails.revision_no);
      return isNaN(num) ? `R${headDetails.revision_no}` : `R${num}`;
    }
    if (
      headDetails?.revision !== undefined &&
      headDetails?.revision !== null &&
      headDetails?.revision !== ""
    ) {
      const revStr = String(headDetails.revision);
      return revStr.toLowerCase().startsWith("r")
        ? revStr.toUpperCase()
        : `R${revStr}`;
    }
    return "R0";
  };

  const rawQuotationType = String(
    headDetails?.type ||
      headDetails?.tran_type ||
      headDetails?.transaction_type ||
      "Sales",
  ).trim();
  const quotationType =
    rawQuotationType.toLowerCase() === "sale"
      ? "Sales"
      : rawQuotationType.replace(/\b\w/g, (character) =>
          character.toUpperCase(),
        );
  const revisionDate = formatDate(new Date());

  return (
    <div className={`invoice-container sales-quotation-layout quotation-page-container${printFormat === "detail" ? " sq-detail-layout" : ""}`}>
      <div className="sq-print-wrapper">
        <div className="sq-grid-header">
          <div className="sq-header-col sq-header-left">
            <div className="invoice-logo-container">
              <img
                src={
                  organizationData.logo_url ||
                  organizationData.logo ||
                  ProfilePlaceholder
                }
                alt="Logo"
                className="invoice-logo-content"
              />
            </div>
            <div className="sq-company-details">
              <div className="sq-org-name">
                {organizationData.name || "FINE PUNCH FAB PRIVATE LIMITED"}
              </div>
              <div>{formatCompanyAddress(organizationData) || "N/A"}</div>
              <div>GSTIN: {organizationData.gst_no || "N/A"}</div>
              <div>State: {organizationData.state || "N/A"}</div>
              <div className="sq-contact-details">
                <div className="sq-contact-row">
                  <span>Email:</span>
                  <span>enquiry@finepunch.in</span>
                </div>
                <div className="sq-contact-row">
                  <span>Website:</span>
                  <span>https://www.finepunch.in</span>
                </div>
              </div>
            </div>
          </div>

          <div className="sq-header-col sq-header-center">
            <div className="sq-doc-title">{quotationType} Quotation</div>
          </div>

          <div className="sq-header-col sq-header-right">
            <div>
              <span className="sq-label">Quote No:</span>
              <span className="sq-val">
                {headDetails?.tran_no ||
                  headDetails?.quote_no ||
                  headDetails?.quotation_no ||
                  ""}
              </span>
            </div>
            <div>
              <span className="sq-label">Quote Date:</span>
              <span className="sq-val">
                {formatDate(
                  headDetails?.date ||
                    headDetails?.created_at ||
                    headDetails?.quote_date,
                )}
              </span>
            </div>
            <div>
              <span className="sq-label">Revision:</span>
              <span className="sq-val">{getRevisionValue()}</span>
            </div>
            <div>
              <span className="sq-label">Reference:</span>
              <span className="sq-val">
                {headDetails?.reference || headDetails?.ref_no || ""}
              </span>
            </div>
          </div>
        </div>

        <div className="sq-grid-middle">
          <div className="sq-mid-col sq-mid-left">
            <div>
              <span className="sq-label">Company Name:</span>
              <span className="sq-val">
                {companyData?.company_name ||
                  companyData?.customer_name ||
                  headDetails?.customer_name ||
                  companyData?.name ||
                  ""}
              </span>
            </div>
            <div>
              <span className="sq-label">Address:</span>
              <span className="sq-val">
                {companyData?.company_address || companyData?.address || ""}
              </span>
            </div>
            <div>
              <span className="sq-label">Contact Person Name:</span>
              <span className="sq-val">
                {companyData?.contact_person ||
                  companyData?.contact_person_name ||
                  companyData?.person_name ||
                  headDetails?.contact_person ||
                  ""}
              </span>
            </div>
            <div>
              <span className="sq-label">Mobile:</span>
              <span className="sq-val">
                {companyData?.contact ||
                  companyData?.company_phone ||
                  companyData?.mobile ||
                  companyData?.phone ||
                  companyData?.contact_phone ||
                  headDetails?.contact ||
                  headDetails?.company_phone ||
                  headDetails?.mobile ||
                  ""}
              </span>
            </div>
            <div>
              <span className="sq-label">GSTIN:</span>
              <span className="sq-val">
                {companyData?.gstno ||
                  companyData?.gst_no ||
                  companyData?.company_gst_no ||
                  headDetails?.gstno ||
                  headDetails?.gst_no ||
                  headDetails?.company_gst_no ||
                  ""}
              </span>
            </div>
          </div>

          <div className="sq-mid-col sq-mid-right">
            <div>
              <span className="sq-label">Terms:</span>
              <span className="sq-val">{headDetails?.terms || ""}</span>
            </div>
            <div>
              <span className="sq-label">Quote Validity:</span>
              <span className="sq-val">
                {headDetails?.quote_validity || headDetails?.validity || ""}
              </span>
            </div>
            <div>
              <span className="sq-label">Taxes:</span>
              <span className="sq-val">
                {headDetails?.taxes || headDetails?.tax || ""}
              </span>
            </div>
            <div>
              <span className="sq-label">Payment Terms:</span>
              <span className="sq-val">{headDetails?.payment_terms || ""}</span>
            </div>
            <div>
              <span className="sq-label">Clause:</span>
              <span className="sq-val">
                {headDetails?.price || headDetails?.price_terms || ""}
              </span>
            </div>
            <div>
              <span className="sq-label">Packing and Forwarding:</span>
              <span className="sq-val">
                {headDetails?.packing_and_forwarding ||
                  headDetails?.pf_charges_terms ||
                  headDetails?.pf_terms ||
                  headDetails?.packing_forwarding ||
                  ""}
              </span>
            </div>
          </div>
        </div>

        <div
          className={`sq-table-wrap${printFormat === "detail" ? " invoice-overflow-x-scroll sq-detail-table-wrap" : ""}`}
          tabIndex={printFormat === "detail" ? 0 : undefined}
          role={printFormat === "detail" ? "region" : undefined}
          aria-label={printFormat === "detail" ? "Quotation items" : undefined}
        >
        <table
          className={`sq-table${printFormat === "detail" ? " sq-detail-table" : ""}`}
          aria-label="Quotation items"
        >
          <thead>
            <tr>
              <th className="sq-col-sl">sl/<wbr />no</th>
              <th className="sq-col-pname">product name</th>
              <th className="sq-col-pdescp">product descp</th>
              <th className="sq-col-uom">uom</th>
              <th className="sq-col-qty">quote qty</th>
              {printFormat === "detail" && detailCostFields.map((field) => (
                <th key={field.label} className="sq-col-cost">{field.label}</th>
              ))}
              <th className="sq-col-rate">rate/<wbr />piece</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(itemDetails) && itemDetails.length > 0
              ? itemDetails.map((item, index) => {
                  const pname =
                    getItemFieldValue(item, [
                      "product_name",
                      "productName",
                      "item_name",
                      "itemName",
                      "product",
                      "item",
                      "title",
                    ]) || "";
                  const pdescp =
                    getItemFieldValue(item, [
                      "product_descp",
                      "product_description",
                      "productDescp",
                      "productDescription",
                      "description",
                      "descp",
                      "item_description",
                      "details",
                    ]) || "";
                  const uom =
                    getItemFieldValue(item, [
                      "uom",
                      "unit",
                      "unit_of_measurement",
                      "units",
                    ]) || "";
                  const qty =
                    getItemFieldValue(item, [
                      "quote_qty",
                      "quoteQty",
                      "qty",
                      "quantity",
                      "order_qty",
                    ]) || "";
                  const rateVal = getItemFieldValue(item, [
                    "rate_piece",
                    "ratePiece",
                    "rate",
                    "price",
                    "unit_price",
                    "unitPrice",
                  ]);
                  const rate = rateVal !== "" ? formatCurrency(rateVal) : "";

                  return (
                    <tr key={index}>
                      <td className="sq-col-sl">{index + 1}</td>
                      <td className="sq-col-pname">{pname}</td>
                      <td className="sq-col-pdescp">{pdescp}</td>
                      <td className="sq-col-uom">{uom}</td>
                      <td className="sq-col-qty">{qty}</td>
                      {printFormat === "detail" && detailCostFields.map((field) => (
                        <td key={field.label} className="sq-col-cost">
                          {getCostValue(item, field)}
                        </td>
                      ))}
                      <td className="sq-col-rate">{rate}</td>
                    </tr>
                  );
                })
              : [1, 2, 3].map((_, i) => (
                  <tr key={i} className="sq-empty-row">
                    <td className="sq-col-sl">{i === 0 ? "1" : ""}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {printFormat === "detail" && detailCostFields.map((field) => (
                      <td key={field.label} className="sq-col-cost"></td>
                    ))}
                    <td></td>
                  </tr>
                ))}
          </tbody>
        </table>
        </div>

        <div className="sq-bottom-section">
          <div className="sq-bottom-row">
            <div className="sq-bottom-left">
              <div className="sq-words-box">
                <span className="sq-label">Net Amount Words:</span>{" "}
                <span className="sq-val">
                  {amountInWords ? `${amountInWords} Only` : ""}
                </span>
              </div>
              <div className="sq-remark-box">
                <span className="sq-label">Remarks:</span>{" "}
                <span className="sq-val">{displayedRemark}</span>
              </div>
            </div>

            <div className="sq-bottom-right">
              <div className="sq-summary-line">
                <span className="sq-label">Freight Charges:</span>
                <span className="sq-val">{formatCurrency(freightCharges)}</span>
              </div>
              <div className="sq-summary-line">
                <span className="sq-label">P & F Charges:</span>
                <span className="sq-val">{formatCurrency(pfCharges)}</span>
              </div>
              <div className="sq-summary-line">
                <span className="sq-label">Gross Total:</span>
                <span className="sq-val">{formatCurrency(grossTotal)}</span>
              </div>
              <div className="sq-summary-line">
                <span className="sq-label">Dis
                  count:</span>
                <span className="sq-val">{formatCurrency(discount)}</span>
              </div>
              <div className="sq-summary-line sq-total-line">
                <span className="sq-label">Total:</span>
                <span className="sq-val">{formatCurrency(netTotal)}</span>
              </div>
            </div>
          </div>

          <div className="sq-footer-area">
            <div className="sq-sign-row">
              <span>e & o.e</span>
              <span>sign</span>
            </div>
          </div>
        </div>
        <div className="sq-preview-meta-footer">
          <div>
            <span className="sq-label">Ref No:</span> FP/SQ/001
          </div>
          <div>
            <span className="sq-label">Rev No:</span> 01
          </div>
          <div>
            <span className="sq-label">Rev Date:</span> {revisionDate || "N/A"}
          </div>
        </div>
      </div>

      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>

      <div className="invoice-buttons" style={{ paddingTop: "20px" }}>
        <DownloadButton
          tableData={invoiceTableData}
          fileName={printFormat === "detail" ? "sales_quotation_detail" : "sales_quotation"}
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".quotation-page-container"
        />
      </div>
      {isFormatOpen && (
        <div className="download-format-modal-overlay">
          <div
            className="download-format-modal-container"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sq-print-format-title"
          >
            <div className="download-format-modal-header">
              <h2 id="sq-print-format-title">Select Print Format</h2>
              <button
                type="button"
                className="download-format-modal-close"
                aria-label="Close print format"
                onClick={() => setIsFormatOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="download-format-modal-content">
              {["summary", "detail"].map((format) => (
                <label key={format} className="sq-print-format-option">
                  <input
                    type="radio"
                    name="sq-print-format"
                    value={format}
                    checked={pendingPrintFormat === format}
                    onChange={() => setPendingPrintFormat(format)}
                  />
                  {format === "summary" ? "Summary Format" : "Detail Format"}
                </label>
              ))}
            </div>
            <div className="download-format-modal-actions">
              <button
                type="button"
                className="download-format-modal-cancel"
                onClick={() => setIsFormatOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="download-format-modal-confirm"
                onClick={() => {
                  setPrintFormat(pendingPrintFormat);
                  setIsFormatOpen(false);
                }}
              >
                Show Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

